import React, { useState, useCallback, useRef, useMemo } from "react";
import { navigate } from "gatsby-link";
import FormSelect from "./elements/form-select";
import FormInput from "./elements/form-input";
import FormText from "./elements/form-text";
import FormAutocomplete from "./elements/form-autocomplete";
import FormPerson from "./elements/form-person";
import FormPrivacy from "./elements/form-privacy";
import AltchaWidget from "../utility/altcha";
import {
  validateDate,
  validateEmail,
  validateTel,
  formatDate,
  validateCode,
} from "../utility/formatters";
import { CDN_BASE_URL } from "../utility/constants";
import { getPrivacyData, submitData } from "../utility/forms";
import { GroupProvider } from "../context/group";
import useBabelModels from "../hooks/use-babel-models";

const initialState = {
  type: "",
  productCategory: "",
  model: "",
  serialNumber: "",
  purchaseDate: "",
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  city: "",
  province: "",
  zipCode: "",
  phone: "",
  mobilePhone: "",
  description: "",
};

const changeFormatters = {
  purchaseDate: formatDate,
};

const Form3 = ({ data, labelManagerLabels }) => {
  const [formData, setFormData] = useState(() => ({
    ...initialState,
    ...getPrivacyData(data),
  }));
  const requestOpts = useMemo(
    () => [
      {
        value: "",
        label: labelManagerLabels.select,
      },
      {
        value: "Technical Support request",
        label: labelManagerLabels.formTechnicalSupportRequest,
      },
      {
        value: "How-to-use and Instructions",
        label: labelManagerLabels.formHowtoUseAndInstructions,
      },
      {
        value: "Product and Accessories Information",
        label: labelManagerLabels.formProductAccessoriesInformation,
      },
    ],
    [labelManagerLabels]
  );
  const matchIndices = useCallback(
    (value, indices) => {
      return indices.includes(requestOpts.findIndex((item) => item.value === value));
    },
    [requestOpts]
  );
  const lineOpts = useMemo(
    () =>
      [
        {
          value: "",
          label: labelManagerLabels.select,
        },
      ].concat(data.lines.selectGroups),
    [data, labelManagerLabels]
  );

  const modelli = useBabelModels(formData.productCategory.split("_")[0]);

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();
  const onChange = useCallback((e) => {
    const { name } = e.target;
    setFormData((current) => {
      const { name, value, type, checked } = e.target;
      let serialNumber, model;
      if (name === "productCategory" && value !== current.productCategory) {
        serialNumber = "";
        model = "";
      }
      const formatter = changeFormatters[name];
      const newValue =
        type === "checkbox" ? checked : formatter ? formatter(e, current[name]) : value;
      return {
        ...current,
        [name]: newValue,
        ...(model !== undefined && { model }),
        ...(serialNumber !== undefined && { serialNumber }),
      };
    });
    setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
  }, []);

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, onChange, formErrors, setFormErrors]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      if (!form.type.value) {
        setFormErrors((current) => ({ ...current, type: true }));
      } else {
        const formElements = [...form.elements]; /* .filter(
          (el) => el.tagName !== "BUTTON" && el.id !== "g-recaptcha-response"
        ) */
        const missingFields = formElements.filter(
          (field) =>
            field.getAttribute &&
            field.getAttribute("data-required") === "true" &&
            (!field.value || (field.type === "checkbox" && !field.checked))
        );
        const invalidFields = [];
        if (form.serialNumber?.value && !validateCode(form.serialNumber?.value)) {
          invalidFields.push("serialNumber");
        }
        if (form.purchaseDate?.value && !validateDate(form.purchaseDate.value)) {
          invalidFields.push("purchaseDate");
        }
        if (form.email?.value && !validateEmail(form.email.value)) {
          invalidFields.push("email");
        }
        if (form.phone?.value && !validateTel(form.phone.value)) {
          invalidFields.push("phone");
        }
        if (form.mobilePhone?.value && !validateTel(form.mobilePhone.value)) {
          invalidFields.push("mobilePhone");
        }

        if (missingFields.length || invalidFields.length) {
          const missingErrs = missingFields.reduce(
            (res, field) => ({
              ...res,
              [field.name]: labelManagerLabels.requiredFields,
            }),
            {}
          );
          const errors = invalidFields.reduce(
            (res, field) => ({ ...res, [field]: true }),
            missingErrs
          );
          setFormErrors((current) => ({ ...current, ...errors }));
          console.warn("Cannot submit: ", errors);
          const firstElError = formElements.find(
            (el) =>
              missingFields.some((field) => el.name === field.name) ||
              invalidFields.includes(el.name)
          );

          var headerOffset =
            document.getElementsByClassName("header")[0].getBoundingClientRect().height + 50;
          var elementPosition = firstElError.getBoundingClientRect().top;
          var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            behavior: "smooth",
            top: offsetPosition,
          });

          return;
        }

        setLoading(true);
        setServerError(false);

        const formPayload = {
          ...formDataRef.current,
          model: modelli.find((item) => item.productName === formDataRef.current.model)?.modelName,
        };

        try {
          const result = await submitData({ ...formPayload }, captcha, data.privacyText, "form3");
          console.log("result: ", result);
          if (data.retUrl) {
            const slug = data.retUrl;
            navigate(slug.startsWith("/") ? slug : `/${slug}`);
          } else {
            navigate("/");
          }
        } catch (err) {
          setServerError(true);
          console.error("Error submitting: ", err);
          altchaRef.current?.reset();
          setCaptcha(null);
        }

        setLoading(false);
      }
    },
    [data.privacyText, data.retUrl, labelManagerLabels, modelli, captcha]
  );

  return (
    <section className="section form">
      <div className="container">
        <div className="row">
          <div className="col-12 px-0">
            <div className="form__container">
              <form className="form" onSubmit={onSubmit}>
                <GroupProvider data={formInfo}>
                  <FormSelect
                    id="request"
                    field="type"
                    label={labelManagerLabels.formRequest}
                    options={requestOpts}
                    isRequired={true}
                  />
                  {matchIndices(formData.type, [1, 3]) && (
                    <>
                      <FormSelect
                        id="line"
                        field="productCategory"
                        label={labelManagerLabels.formProductLine}
                        options={lineOpts}
                        isRequired={true}
                      />
                      {formData.productCategory &&
                        (!modelli ? (
                          <div className="py-4 d-flex align-items-center justify-content-center">
                            <div className="spinner-border"></div>
                          </div>
                        ) : (
                          <>
                            <FormAutocomplete
                              id="productModel"
                              field="model"
                              maxLength={20}
                              options={modelli ? modelli.map((item) => item.productName) : []}
                              isRequired={true}
                              label={labelManagerLabels.formModel}
                              autoSelect={false}
                            />
                            <FormInput
                              id="productCode"
                              field="serialNumber"
                              maxLength={20}
                              label={`${
                                labelManagerLabels.formProductCode ||
                                labelManagerLabels.serialNumber
                              } *`}
                              isRequired={true}
                              validate={validateCode}
                              formatWarning={labelManagerLabels.serialNumberValidationHaier}
                            />
                          </>
                        ))}

                      {formData.productCategory && (
                        <div className="form__product d-flex flex-wrap align-items-start">
                          <p className="form__label">{labelManagerLabels.bookletFindImage}</p>
                          <img
                            alt={labelManagerLabels.formProductLine}
                            src={`${CDN_BASE_URL}/booklet/libretto-${
                              formData.productCategory.split("_")[0]
                            }.png`}
                          />
                          <img
                            alt={labelManagerLabels.howReadProductCode}
                            src={`${CDN_BASE_URL}/booklet/libretto-label.png`}
                          />
                        </div>
                      )}

                      <FormInput
                        id="purchaseDate"
                        field="purchaseDate"
                        maxLength={10}
                        placeholder={`${
                          labelManagerLabels.formPurchaseDate + " " + labelManagerLabels.dateFormat
                        } *`}
                        isRequired={true}
                        validate={validateDate}
                        formatWarning={labelManagerLabels.validDate}
                      />

                      <FormPerson labelManagerLabels={labelManagerLabels} />
                      <FormText
                        id="description"
                        field="description"
                        maxLength={32000}
                        placeholder={`${labelManagerLabels.formComments} *`}
                        isRequired={true}
                        rows={10}
                      />

                      <FormPrivacy
                        data={data.privacyText}
                        labelManagerLabels={labelManagerLabels}
                      />

                      <div className="my-5">
                        <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                      </div>
                      <div className="text-center">
                        <button className="btn-link button primary" disabled={!captcha || loading}>
                          {labelManagerLabels.send}
                        </button>
                        {serverError && (
                          <p className="mt-2 form__service-not-available">
                            {labelManagerLabels.serviceNotAvailable}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {matchIndices(formData.type, [2]) && (
                    <div
                      className="form__block p-3 pb-4"
                      dangerouslySetInnerHTML={{ __html: labelManagerLabels.bookletSearchHaier }}
                    ></div>
                  )}

                  <div className="form__info mt-4">* {labelManagerLabels.requiredFields}</div>
                </GroupProvider>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form3;
